import { createSlice } from '@reduxjs/toolkit';
import { employeeApi } from '../../services/employeeApi';
import { mockEmployeeApi } from '../../services/mockEmployeeApi';

// Use mock data for development/design checking
const USE_MOCK_DATA = process.env.REACT_APP_USE_MOCK_DATA === 'true';

const initialState = {
  employees: [],
  currentEmployee: null,
  loading: false,
  error: null,
  filters: {
    department: '',
    designation: '',
    status: '',
    search: '',
  },
  pagination: {
    currentPage: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  stats: {
    totalEmployees: 0,
    activeEmployees: 0,
    inactiveEmployees: 0,
    onLeaveEmployees: 0,
    newHiresThisMonth: 0,
  },
};

const employeeSlice = createSlice({
  name: 'employee',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.currentPage = 1; // Reset to first page when filters change
    },
    clearFilters: (state) => {
      state.filters = {
        department: '',
        designation: '',
        status: '',
        search: '',
      };
      state.pagination.currentPage = 1;
    },
    setCurrentPage: (state, action) => {
      state.pagination.currentPage = action.payload;
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setStats: (state, action) => {
      state.stats = action.payload;
    },
    setCurrentEmployee: (state, action) => {
      state.currentEmployee = action.payload;
    },
    clearCurrentEmployee: (state) => {
      state.currentEmployee = null;
    },
    setEmployees: (state, action) => {
      state.employees = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Handle API responses to update stats
    builder
      .addMatcher(
        employeeApi.endpoints.getEmployee.matchFulfilled,
        (state, action) => {
          // RTK Query responses may wrap data under `data` (server: { success, data: { employee } })
          state.currentEmployee = action.payload?.data?.employee || action.payload?.employee || null;
        }
      )
      .addMatcher(
        employeeApi.endpoints.createEmployee.matchFulfilled,
        (state, action) => {
          // Refresh stats when employee is created
          state.stats.totalEmployees += 1;
          const newEmployeeStatus = action.payload?.data?.employee?.status || 'active';
          if (newEmployeeStatus === 'active') {
            state.stats.activeEmployees += 1;
          } else if (newEmployeeStatus === 'on-leave') {
            state.stats.onLeaveEmployees += 1;
          } else if (newEmployeeStatus === 'inactive') {
            state.stats.inactiveEmployees += 1;
          }
        }
      )
      .addMatcher(
        employeeApi.endpoints.deleteEmployee.matchFulfilled,
        (state, action) => {
          // Refresh stats when employee is deleted
          state.stats.totalEmployees -= 1;
          // Note: We can't determine the deleted employee's status from the action,
          // so we'll need to refresh stats from the server
        }
      );
  },
});

export const {
  setFilters,
  clearFilters,
  setCurrentPage,
  setPagination,
  setStats,
  setCurrentEmployee,
  clearCurrentEmployee,
  setEmployees,
} = employeeSlice.actions;

// Selectors
export const selectEmployeeFilters = (state) => state.employee.filters;
export const selectEmployeePagination = (state) => state.employee.pagination;
export const selectEmployeeStats = (state) => state.employee.stats;

// Thunks for API integration
export const fetchEmployees = (params) => async (dispatch, getState) => {
  try {
    console.log('fetchEmployees called with params:', params);
    let result;

    if (USE_MOCK_DATA) {
      result = await mockEmployeeApi.getEmployees(params);
    } else {
      const queryResult = await dispatch(
        employeeApi.endpoints.getEmployees.initiate(params)
      );
      // Properly unwrap the RTK Query result
      result = await queryResult.unwrap();
      console.log('RTK Query result:', result);
    }

    console.log('Final result structure:', result);
    console.log('Employees to store:', result.data?.employees || result.employees);

    // Update pagination
    dispatch(setPagination({
      currentPage: result.data?.pagination?.page || 1,
      total: result.data?.pagination?.total || 0,
      totalPages: Math.ceil((result.data?.pagination?.total || 0) / (result.data?.pagination?.limit || 10)),
    }));

    // Store employees data
    if (USE_MOCK_DATA) {
      dispatch(setEmployees(result.employees));
      dispatch(setStats(result.stats));
    } else {
      // result is expected to be the API response: { success, data: { employees, pagination, stats } }
      dispatch(setEmployees(result.data?.employees || []));
      dispatch(setStats(result.data?.stats || {}));
    }

    console.log('Employees dispatched to store');
    return result;
  } catch (error) {
    console.error('Error fetching employees:', error);
    throw error;
  }
};

export const createEmployee = (employeeData) => async (dispatch) => {
  try {
    let result;

    if (USE_MOCK_DATA) {
      result = await mockEmployeeApi.createEmployee(employeeData);
    } else {
      result = await dispatch(
        employeeApi.endpoints.createEmployee.initiate(employeeData)
      );
      result = result?.data || (await result.unwrap?.());
    }

    return result;
  } catch (error) {
    console.error('Error creating employee:', error);
    throw error;
  }
};

export const updateEmployee = ({ id, employeeData }) => async (dispatch) => {
  try {
    let result;

    if (USE_MOCK_DATA) {
      result = await mockEmployeeApi.updateEmployee(id, employeeData);
    } else {
      result = await dispatch(
        employeeApi.endpoints.updateEmployee.initiate({ id, ...employeeData })
      );
      result = result?.data || (await result.unwrap?.());
    }

    return result;
  } catch (error) {
    console.error('Error updating employee:', error);
    throw error;
  }
};

export const fetchEmployeeById = (id) => async (dispatch) => {
  try {
    let result;

    if (USE_MOCK_DATA) {
      result = await mockEmployeeApi.getEmployee(id);
    } else {
      const initiated = await dispatch(
        employeeApi.endpoints.getEmployee.initiate(id)
      );
      result = initiated?.data || (await initiated.unwrap?.());
    }

    // Normalize: backend may return { success, data: { employee } } or { employee }
    const employee = result?.data?.employee || result?.employee || result;
    dispatch(setCurrentEmployee(employee));
    return { employee };
  } catch (error) {
    console.error('Error fetching employee:', error);
    throw error;
  }
};

export const deleteEmployee = (id) => async (dispatch) => {
  try {
    let result;

    if (USE_MOCK_DATA) {
      result = await mockEmployeeApi.deleteEmployee(id);
    } else {
      result = await dispatch(
        employeeApi.endpoints.deleteEmployee.initiate(id)
      ).unwrap();
    }

    return result;
  } catch (error) {
    console.error('Error deleting employee:', error);
    throw error;
  }
};

export default employeeSlice.reducer;
